import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Alert,
  Snackbar
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { supabase } from "../../../supabaseClient"; 
import useAlert from "../../../alert/useAlert";

// Styled components for drag and drop areas
const DraggableItem = styled(Card)(({ theme, isDragging }) => ({
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: isDragging ? "#e0e0e0" : "#FFFFFF",
  cursor: "grab",
  borderRadius: 4,
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#e0e0e0",
  }
}));

const DropArea = styled(Box)(({ theme, isDraggingOver, isEmpty }) => ({
  height: "70px",
  margin: theme.spacing(0.5),
  padding: theme.spacing(1),
  backgroundColor: isDraggingOver ? "#bbdefb" : isEmpty ? "#f5f5f5" : "#FFFFFF",
  border: `1px dashed ${isDraggingOver ? "#1976d2" : "#bdbdbd"}`,
  borderRadius: 4,
  transition: "background-color 0.2s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ImageUploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #ccc',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '120px',
  cursor: 'pointer',
  backgroundColor: '#fafafa',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#29C0BB',
    backgroundColor: '#f0f0f0',
  },
}));

// Mock data for organization
// const organizationData = {
//   name: "Tikcare Medical Center",
//   email: "contact@tikcare.com",
//   phone: "+1 (555) 123-4567",
//   address: "123 Healthcare Ave, Medical District, CA 90210",
// };

const TemplateLayout = ({ handleBack, userDetails }) => {
  const userId = userDetails.id;
  const practice_type = userDetails.practicetype;
  const { setAlert } = useAlert();

  const [organizationData, setOrganizationData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    practice_type: "",
    practice_title: "",
    website: ""
  });
  
  // State for draggable items
  const [headerItems, setHeaderItems] = useState([
    { id: "h-name", content: "Organization Name" },
    { id: "h-email", content: "Email" },
    { id: "h-phone", content: "Phone Number" },
    { id: "h-address", content: "Address" },
    { id: "h-practice-type", content: "Practice Type" },
    { id: "h-practice-title", content: "Practice Title" },
    { id: "h-website", content: "Website" },
    { id: "h-logo", content: "Logo" },
    { id: "h-signature", content: "Signature" },
    { id: "h-custom1", content: "Custom Image 1" },
    { id: "h-custom2", content: "Custom Image 2" },
  ]);
  
  const [footerItems, setFooterItems] = useState([
    { id: "f-name", content: "Organization Name" },
    { id: "f-email", content: "Email" },
    { id: "f-phone", content: "Phone Number" },
    { id: "f-address", content: "Address" },
    { id: "f-practice-type", content: "Practice Type" },
    { id: "f-practice-title", content: "Practice Title" },
    { id: "f-website", content: "Website" },
    { id: "f-logo", content: "Logo" },
    { id: "f-signature", content: "Signature" },
    { id: "f-custom1", content: "Custom Image 1" },
    { id: "f-custom2", content: "Custom Image 2" },
  ]);

  // State for header and footer grid placements
  const [headerGrid, setHeaderGrid] = useState(Array(15).fill(null));
  const [footerGrid, setFooterGrid] = useState(Array(15).fill(null));
  
  // State for preview dialogs
  const [headerPreviewOpen, setHeaderPreviewOpen] = useState(false);
  const [footerPreviewOpen, setFooterPreviewOpen] = useState(false);
  
  // State for image uploads
  const [logoImage, setLogoImage] = useState(null);
  const [signatureImage, setSignatureImage] = useState(null);
  const [customImage1, setCustomImage1] = useState(null);
  const [customImage2, setCustomImage2] = useState(null);
  
  // State for image URLs
  const [logoUrl, setLogoUrl] = useState("");
  const [signatureUrl, setSignatureUrl] = useState("");
  const [customImage1Url, setCustomImage1Url] = useState("");
  const [customImage2Url, setCustomImage2Url] = useState("");
  
  // // State for notifications
  // const [notification, setNotification] = useState({ open: false, message: "", severity: "info" });
  
  // Fetch existing template layout data on component mount
  useEffect(() => {
    fetchTemplateData();
    fetchOrganizationData();
  }, [userId]);

  const fetchTemplateData = async () => {
    try {
      const { data, error } = await supabase
        .from('templatelayout')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
        
      if (error) throw error;
      
      if (data) {
        // Populate data from database
        if (data.header_layout) setHeaderGrid(JSON.parse(data.header_layout));
        if (data.footer_layout) setFooterGrid(JSON.parse(data.footer_layout));
        if (data.logo_url) setLogoUrl(data.logo_url);
        if (data.signature_url) setSignatureUrl(data.signature_url);
        if (data.custom_image1_url) setCustomImage1Url(data.custom_image1_url);
        if (data.custom_image2_url) setCustomImage2Url(data.custom_image2_url);
        
        // Update available items lists based on what's already used
        updateAvailableItems(JSON.parse(data.header_layout), JSON.parse(data.footer_layout));
      }
    } catch (error) {
      console.error('Error fetching template data:', error);
    }
  };

  const fetchOrganizationData = async () => {
    try {
      const { data, error } = await supabase
        .from('user_accounts')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching practice info:', error);
        return;
      }
      
      if (data) {
        const [practiceTypeResult, practiceTitleResult, organisationResult, addressResult, phoneNumberResult, websiteResult, emailResult] = await Promise.all([
          supabase.rpc('decrypt_text', { encrypted_data: data.practicetype }),
          supabase.rpc('decrypt_text', { encrypted_data: data.practice_title }),
          supabase.rpc('decrypt_text', { encrypted_data: data.organisation }),
          supabase.rpc('decrypt_text', { encrypted_data: data.address }),
          supabase.rpc('decrypt_text', { encrypted_data: data.phone_number }),
          supabase.rpc('decrypt_text', { encrypted_data: data.website }),
          supabase.rpc('decrypt_text', { encrypted_data: data.secondary_email })
        ]);
        if(practiceTitleResult.error || organisationResult.error || addressResult.error || phoneNumberResult.error || websiteResult.error || emailResult.error) {
          console.error('Error decrypting practice info:', practiceTitleResult.error || organisationResult.error || addressResult.error || phoneNumberResult.error || websiteResult.error || emailResult.error);
          return;
        }

        setOrganizationData({
          name: organisationResult.data || "",
          email: emailResult.data || "",
          phone: phoneNumberResult.data || "",
          address: addressResult.data || "",
          practice_type: practiceTypeResult.data || "",
          practice_title: practiceTitleResult.data || "",
          website: websiteResult.data || ""
        });
      }
    } catch (error) {
      console.error('Unexpected error fetching organization data:', error);
    }
  };
  
  // Function to update available items based on what's already placed
  const updateAvailableItems = (headerLayout, footerLayout) => {
    const usedIds = [
      ...headerLayout.filter(item => item !== null).map(item => item.id),
      ...footerLayout.filter(item => item !== null).map(item => item.id)
    ];
    
    // Filter out items that are already placed
    const newHeaderItems = headerItems.filter(item => !usedIds.includes(item.id));
    const newFooterItems = footerItems.filter(item => !usedIds.includes(item.id));
    
    setHeaderItems(newHeaderItems);
    setFooterItems(newFooterItems);
  };

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    
    // Get the original item ID by removing the 'grid-' prefix if present
    const originalId = draggableId.startsWith('grid-') ? draggableId.substring(5) : draggableId;
    
    // Return if dropped outside a droppable area
    if (!destination || 
      (source.droppableId === destination.droppableId && 
       source.index === destination.index)) {
      return;
    }
    
    // Handle moving between header items and grid
    if (source.droppableId === 'header-items' && destination.droppableId.startsWith('header-grid-')) {
      // Find the item from header items
      const itemIndex = headerItems.findIndex(item => item.id === originalId);
      const item = headerItems[itemIndex];
      
      // Remove from header items
      const newHeaderItems = Array.from(headerItems);
      newHeaderItems.splice(itemIndex, 1);
      
      // Get the grid index
      const gridIndex = parseInt(destination.droppableId.split('-')[2]);
      const newHeaderGrid = Array.from(headerGrid);
      
      // Check if there's already an item in the destination
      const existingItem = newHeaderGrid[gridIndex];
      if (existingItem) {
        // Return the existing item to the header items list
        newHeaderItems.push(existingItem);
      }
      
      // Add new item to header grid
      newHeaderGrid[gridIndex] = item;
      
      // Update both states
      setHeaderItems(newHeaderItems);
      setHeaderGrid(newHeaderGrid);
    }
    // Handle moving from header grid to items
    else if (source.droppableId.startsWith('header-grid-') && destination.droppableId === 'header-items') {
      // Get the grid index
      const gridIndex = parseInt(source.droppableId.split('-')[2]);
      const item = headerGrid[gridIndex];
      
      // Remove from grid
      const newHeaderGrid = Array.from(headerGrid);
      newHeaderGrid[gridIndex] = null;
      setHeaderGrid(newHeaderGrid);
      
      // Add to items
      const newHeaderItems = Array.from(headerItems);
      newHeaderItems.push(item);
      setHeaderItems(newHeaderItems);
    }
    // Handle moving between footer items and grid
    else if (source.droppableId === 'footer-items' && destination.droppableId.startsWith('footer-grid-')) {
      // Find the item from footer items
      const itemIndex = footerItems.findIndex(item => item.id === originalId);
      const item = footerItems[itemIndex];
      
      // Remove from footer items
      const newFooterItems = Array.from(footerItems);
      newFooterItems.splice(itemIndex, 1);
      
      // Get the grid index
      const gridIndex = parseInt(destination.droppableId.split('-')[2]);
      const newFooterGrid = Array.from(footerGrid);
      
      // Check if there's already an item in the destination
      const existingItem = newFooterGrid[gridIndex];
      if (existingItem) {
        // Return the existing item to the footer items list
        newFooterItems.push(existingItem);
      }
      
      // Add new item to footer grid
      newFooterGrid[gridIndex] = item;
      
      // Update both states
      setFooterItems(newFooterItems);
      setFooterGrid(newFooterGrid);
    }
    // Handle moving from footer grid to items
    else if (source.droppableId.startsWith('footer-grid-') && destination.droppableId === 'footer-items') {
      // Get the grid index
      const gridIndex = parseInt(source.droppableId.split('-')[2]);
      const item = footerGrid[gridIndex];
      
      // Remove from grid
      const newFooterGrid = Array.from(footerGrid);
      newFooterGrid[gridIndex] = null;
      setFooterGrid(newFooterGrid);
      
      // Add to items
      const newFooterItems = Array.from(footerItems);
      newFooterItems.push(item);
      setFooterItems(newFooterItems);
    }
    // Handle moving between grid cells (header)
    else if (source.droppableId.startsWith('header-grid-') && destination.droppableId.startsWith('header-grid-')) {
      const sourceIndex = parseInt(source.droppableId.split('-')[2]);
      const destIndex = parseInt(destination.droppableId.split('-')[2]);
      
      const newHeaderGrid = Array.from(headerGrid);
      const sourceItem = newHeaderGrid[sourceIndex];
      const destItem = newHeaderGrid[destIndex];
      
      // If destination already has an item, swap positions
      if (destItem) {
        newHeaderGrid[sourceIndex] = destItem;
        newHeaderGrid[destIndex] = sourceItem;
      } else {
        // Just move the item
        newHeaderGrid[sourceIndex] = null;
        newHeaderGrid[destIndex] = sourceItem;
      }
      
      setHeaderGrid(newHeaderGrid);
    }
    // Handle moving between grid cells (footer)
    else if (source.droppableId.startsWith('footer-grid-') && destination.droppableId.startsWith('footer-grid-')) {
      const sourceIndex = parseInt(source.droppableId.split('-')[2]);
      const destIndex = parseInt(destination.droppableId.split('-')[2]);
      
      const newFooterGrid = Array.from(footerGrid);
      const sourceItem = newFooterGrid[sourceIndex];
      const destItem = newFooterGrid[destIndex];
      
      // If destination already has an item, swap positions
      if (destItem) {
        newFooterGrid[sourceIndex] = destItem;
        newFooterGrid[destIndex] = sourceItem;
      } else {
        // Just move the item
        newFooterGrid[sourceIndex] = null;
        newFooterGrid[destIndex] = sourceItem;
      }
      
      setFooterGrid(newFooterGrid);
    }
  };
  
  // Handle image upload for logo, signature, and custom images
  const handleImageUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (limit to 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setAlert("Image is too large. Please upload an image smaller than 2MB.", 'error');
      return;
    }
    
    // Update local state
    switch (type) {
      case 'logo':
        setLogoImage(file);
        break;
      case 'signature':
        setSignatureImage(file);
        break;
      case 'custom1':
        setCustomImage1(file);
        break;
      case 'custom2':
        setCustomImage2(file);
        break;
      default:
        return;
    }
    
    // Create a preview URL
    const imageUrl = URL.createObjectURL(file);
    
    switch (type) {
      case 'logo':
        setLogoUrl(imageUrl);
        break;
      case 'signature':
        setSignatureUrl(imageUrl);
        break;
      case 'custom1':
        setCustomImage1Url(imageUrl);
        break;
      case 'custom2':
        setCustomImage2Url(imageUrl);
        break;
      default:
        return;
    }
    setAlert("Image selected successfully. It will be uploaded when you save.", 'success');
  };
  
  // Function to upload images to Supabase storage
  const uploadImagesToStorage = async () => {
    const uploadPromises = [];
    const imageUrls = {
      logoUrl: logoUrl,
      signatureUrl: signatureUrl,
      customImage1Url: customImage1Url,
      customImage2Url: customImage2Url
    };
    
    // Upload logo if changed
    if (logoImage) {
      const filePath = `template-layout/${userId}/logo-${Date.now()}`;
      uploadPromises.push(
        supabase.storage
          .from('tikcare-attachments')
          .upload(filePath, logoImage, { upsert: true })
          .then(({ data, error }) => {
            if (error) throw error;
            // Get public URL directly (not as a Promise)
            const { data: urlData } = supabase.storage
              .from('tikcare-attachments')
              .getPublicUrl(filePath);
            
            imageUrls.logoUrl = urlData.publicUrl;
          })
      );
    }
    
    // Upload signature if changed
    if (signatureImage) {
      const filePath = `template-layout/${userId}/signature-${Date.now()}`;
      uploadPromises.push(
        supabase.storage
          .from('tikcare-attachments')
          .upload(filePath, signatureImage, { upsert: true })
          .then(({ data, error }) => {
            if (error) throw error;
            // Get public URL directly
            const { data: urlData } = supabase.storage
              .from('tikcare-attachments')
              .getPublicUrl(filePath);
            
            imageUrls.signatureUrl = urlData.publicUrl;
          })
      );
    }
    
    // Upload custom image 1 if changed
    if (customImage1) {
      const filePath = `template-layout/${userId}/custom1-${Date.now()}`;
      uploadPromises.push(
        supabase.storage
          .from('tikcare-attachments')
          .upload(filePath, customImage1, { upsert: true })
          .then(({ data, error }) => {
            if (error) throw error;
            // Get public URL directly
            const { data: urlData } = supabase.storage
              .from('tikcare-attachments')
              .getPublicUrl(filePath);
            
            imageUrls.customImage1Url = urlData.publicUrl;
          })
      );
    }
    
    // Upload custom image 2 if changed
    if (customImage2) {
      const filePath = `template-layout/${userId}/custom2-${Date.now()}`;
      uploadPromises.push(
        supabase.storage
          .from('tikcare-attachments')
          .upload(filePath, customImage2, { upsert: true })
          .then(({ data, error }) => {
            if (error) throw error;
            // Get public URL directly
            const { data: urlData } = supabase.storage
              .from('tikcare-attachments')
              .getPublicUrl(filePath);
            
            imageUrls.customImage2Url = urlData.publicUrl;
          })
      );
    }
    
    try {
      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
      return imageUrls;
    } catch (error) {
      console.error("Error uploading images:", error);
      throw error; // Rethrow to be handled by the calling function
    }
  };
  
  // Handle saving all settings
  const handleSave = async () => {
    try {
      // First upload all images to storage
      const imageUrls = await uploadImagesToStorage();
      
      // Save template layout data to database
      const { error } = await supabase
        .from('templatelayout')
        .upsert({
          id: userId,
          header_layout: JSON.stringify(headerGrid),
          footer_layout: JSON.stringify(footerGrid),
          logo_url: imageUrls.logoUrl,
          signature_url: imageUrls.signatureUrl,
          custom_image1_url: imageUrls.customImage1Url,
          custom_image2_url: imageUrls.customImage2Url,
          organization_data: JSON.stringify(organizationData),
          updated_at: new Date()
        }, { onConflict: 'id' });
        
      if (error) throw error;
      
      setAlert("Template layout saved successfully", 'success');   
    } catch (error) {
      console.error('Error saving template layout:', error);
      setAlert("Failed to save template layout: " + error.message, 'error');
    }
  };
  
  // Render element for preview
  const renderPreviewItem = (item) => {
    if (!item) return null;

    const getContent = () => {
      switch(item.id.replace(/^[hf]-/, "")) {
        // Text content cases
        case "name":
        case "email":
        case "phone":
        case "address":
        case "practice-type":
        case "practice-title":
        case "website":
          return (
            <Typography variant="body2" sx={{ 
              whiteSpace: 'pre-line',
              display: 'block',
              width: '100%'
            }}>
              {organizationData[item.id.replace(/^[hf]-/, "").replace(/-/g, "_")] || item.content}
            </Typography>
          );
        
        // Image content cases  
        case "logo":
          return logoUrl ? 
            <Box component="img" src={logoUrl} alt="Logo" sx={{ maxHeight: 95, maxWidth: '100%' }} /> : 
            <Box sx={{ bgcolor: '#eee', height: 60, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" color="textSecondary">Logo</Typography>
            </Box>;
        case "signature":
          return signatureUrl ?
            <Box component="img" src={signatureUrl} alt="Signature" sx={{ maxHeight: 60, maxWidth: '100%' }} /> : 
            <Box sx={{ bgcolor: '#eee', height: 60, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" color="textSecondary">Signature</Typography>
            </Box>;
        case "custom1":
          return customImage1Url ?
            <Box component="img" src={customImage1Url} alt="Custom Image 1" sx={{ maxHeight: 60, maxWidth: '100%' }} /> :
            <Box sx={{ bgcolor: '#eee', height: 60, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>  
              <Typography variant="caption" color="textSecondary">Custom Image 1</Typography>
            </Box>;
        case "custom2":
          return customImage2Url ?
            <Box component="img" src={customImage2Url} alt="Custom Image 2" sx={{ maxHeight: 60, maxWidth: '100%' }} /> : 
            <Box sx={{ bgcolor: '#eee', height: 60, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>  
              <Typography variant="caption" color="textSecondary">Custom Image 2</Typography> 
            </Box>;        
        default:
          return <Typography variant="body2">{item.content}</Typography>;
      }
    }

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        width: '100%',
        height: '100%'
      }}>
        {getContent()}
      </Box>
    );
  };

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        overflowAnchor: 'none', // Prevents browser from scrolling to newly focused elements
        height: '100%'
      }}
    >
      <Paper sx={{ p: 3, my: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h5">
            Template Layout Settings
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button 
              variant="outlined" 
              onClick={handleBack}
              sx={{ 
                borderRadius: 2,
                color: "#29C0BB",
                borderColor: "#29C0BB",
                "&:hover": {
                  borderColor: "#1da8a3",
                }
              }}
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              sx={{ borderRadius: 2, bgcolor: "#29C0BB" }}
            >
              Save
            </Button>
          </Stack>
        </Box>
        
        <DragDropContext onDragEnd={handleDragEnd}>
          {/* Header Section */}
          <Paper elevation={1} sx={{ p: 2, mb: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Header</Typography>
              <Button 
                variant="outlined" 
                onClick={() => setHeaderPreviewOpen(true)}
                sx={{ 
                  borderRadius: 5,
                  color: "#29C0BB",
                  borderColor: "#29C0BB",
                  "&:hover": {
                    borderColor: "#1da8a3",
                  }
                }}
              >
                Preview
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              {/* Left side - Draggable Items */}
              <Grid size={{xs: 12, md: 3}}>
                <Paper 
                  elevation={0} 
                  variant="outlined"
                  sx={{ 
                    p: 2, 
                    height: '300px', 
                    overflowY: 'auto',
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <Typography variant="subtitle2" mb={1}>Available Items</Typography>
                  <Droppable droppableId="header-items">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ minHeight: '250px' }}
                      >
                        {headerItems.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided, snapshot) => (
                              <DraggableItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                              >
                                <Typography variant="body2">{item.content}</Typography>
                              </DraggableItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {headerItems.length === 0 && (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              All items have been placed
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Droppable>
                </Paper>
              </Grid>
              
              {/* Right side - Grid Layout */}
              <Grid size={{xs: 12, md: 9}}>
                <Paper 
                  elevation={0} 
                  variant="outlined"
                  sx={{ 
                    p: 2, 
                    height: '400px',
                    backgroundColor: '#f5f5f5'
                  }}
                >
                  <Grid container spacing={1}>
                    {/* Create 3 columns with 5 rows each */}
                    {Array.from({ length: 3 }).map((_, colIndex) => (
                      <Grid size={4} key={`header-col-${colIndex}`}>
                        {Array.from({ length: 5 }).map((_, rowIndex) => {
                          const gridIndex = colIndex * 5 + rowIndex;
                          const gridItem = headerGrid[gridIndex];
                          
                          return (
                            <Droppable droppableId={`header-grid-${gridIndex}`} key={`header-grid-${gridIndex}`}>
                              {(provided, snapshot) => (
                                <DropArea
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  isDraggingOver={snapshot.isDraggingOver}
                                  isEmpty={!gridItem}
                                >
                                  {gridItem ? (
                                    <Draggable 
                                      draggableId={`grid-${gridItem.id}`}
                                      index={0}
                                      key={gridItem.id}
                                    >
                                      {(provided, snapshot) => (
                                        <Box
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          sx={{ width: '100%', textAlign: 'center' }}
                                        >
                                          <Typography variant="body2">{gridItem.content}</Typography>
                                        </Box>
                                      )}
                                    </Draggable>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      Drop here
                                    </Typography>
                                  )}
                                  {provided.placeholder}
                                </DropArea>
                              )}
                            </Droppable>
                          );
                        })}
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Footer Section */}
          <Paper elevation={1} sx={{ p: 2, mb: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Footer</Typography>
              <Button 
                variant="outlined" 
                onClick={() => setFooterPreviewOpen(true)}
                sx={{ 
                  borderRadius: 5,
                  color: "#29C0BB",
                  borderColor: "#29C0BB",
                  "&:hover": {
                    borderColor: "#1da8a3",
                  }
                }}
              >
                Preview
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              {/* Left side - Draggable Items */}
              <Grid size={{xs: 12, md: 3}}>
                <Paper 
                  elevation={0} 
                  variant="outlined"
                  sx={{ 
                    p: 2, 
                    height: '300px', 
                    overflowY: 'auto',
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <Typography variant="subtitle2" mb={1}>Available Items</Typography>
                  <Droppable droppableId="footer-items">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ minHeight: '250px' }}
                      >
                        {footerItems.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided, snapshot) => (
                              <DraggableItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                              >
                                <Typography variant="body2">{item.content}</Typography>
                              </DraggableItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {footerItems.length === 0 && (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              All items have been placed
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Droppable>
                </Paper>
              </Grid>
              
              {/* Right side - Grid Layout */}
              <Grid size={{xs: 12, md: 9}}>
                <Paper 
                  elevation={0} 
                  variant="outlined"
                  sx={{ 
                    p: 2, 
                    height: '400px',
                    backgroundColor: '#f5f5f5'
                  }}
                >
                  <Grid container spacing={1}>
                    {/* Create 3 columns with 5 rows each */}
                    {Array.from({ length: 3 }).map((_, colIndex) => (
                      <Grid size={4} key={`footer-col-${colIndex}`}>
                        {Array.from({ length: 5 }).map((_, rowIndex) => {
                          const gridIndex = colIndex * 5 + rowIndex;
                          const gridItem = footerGrid[gridIndex];
                          
                          return (
                            <Droppable droppableId={`footer-grid-${gridIndex}`} key={`footer-grid-${gridIndex}`}>
                              {(provided, snapshot) => (
                                <DropArea
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  isDraggingOver={snapshot.isDraggingOver}
                                  isEmpty={!gridItem}
                                >
                                    {gridItem ? (
                                      <Draggable 
                                        draggableId={`grid-${gridItem.id}`}
                                        index={0}
                                        key={gridItem.id}
                                      >
                                        {(provided, snapshot) => (
                                          <Box
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            sx={{ width: '100%', textAlign: 'center' }}
                                          >
                                            <Typography variant="body2">{gridItem.content}</Typography>
                                          </Box>
                                        )}
                                      </Draggable>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        Drop here
                                      </Typography>
                                    )}
                                  {provided.placeholder}
                                </DropArea>
                              )}
                            </Droppable>
                          );
                        })}
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </DragDropContext>
        
        {/* Default Images Section */}
        <Paper elevation={1} sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" mb={3}>Default Images</Typography>
          
          {/* Logo Upload */}
          <Box mb={4}>
            <Typography variant="subtitle1" mb={1}>Your Logo</Typography>
            <ImageUploadBox>
              {logoUrl ? (
                <>
                  <Box component="img" src={logoUrl} alt="Logo" sx={{ height: 60, mb: 1 }} />
                  <Button 
                    component="label" 
                    size="small" 
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{ 
                      color: "#29C0BB",
                      borderColor: "#29C0BB",
                      "&:hover": {
                        borderColor: "#1da8a3",
                      }
                    }}
                  >
                    Change
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo')}
                    />
                  </Button>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: "#999", mb: 1 }} />
                  <Button 
                    component="label" 
                    variant="outlined"
                    size="small"
                    sx={{ 
                      color: "#29C0BB",
                      borderColor: "#29C0BB",
                      "&:hover": {
                        borderColor: "#1da8a3",
                      }
                    }}
                  >
                    Upload Logo (max 2MB)
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo')}
                    />
                  </Button>
                </>
              )}
            </ImageUploadBox>
          </Box>
          
          {/* Signature Upload */}
          <Box mb={4}>
            <Typography variant="subtitle1" mb={1}>Your Signature</Typography>
            <ImageUploadBox>
              {signatureUrl ? (
                <>
                  <Box component="img" src={signatureUrl} alt="Signature" sx={{ height: 60, mb: 1 }} />
                  <Button 
                    component="label" 
                    size="small" 
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{ 
                      color: "#29C0BB",
                      borderColor: "#29C0BB",
                      "&:hover": {
                        borderColor: "#1da8a3",
                      }
                    }}
                  >
                    Change
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'signature')}
                    />
                  </Button>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: "#999", mb: 1 }} />
                  <Button 
                    component="label" 
                    variant="outlined"
                    size="small"
                    sx={{ 
                      color: "#29C0BB",
                      borderColor: "#29C0BB",
                      "&:hover": {
                        borderColor: "#1da8a3",
                      }
                    }}
                  >
                    Upload Signature (max 2MB)
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'signature')}
                    />
                  </Button>
                </>
              )}
            </ImageUploadBox>
          </Box>
          
          {/* Custom Images Upload */}
          <Grid container spacing={3}>
            <Grid size={{xs: 12, md: 6}}>
              <Typography variant="subtitle1" mb={1}>Custom Image 1</Typography>
              <ImageUploadBox>
                {customImage1Url ? (
                  <>
                    <Box component="img" src={customImage1Url} alt="Custom Image 1" sx={{ height: 60, mb: 1 }} />
                    <Button 
                      component="label" 
                      size="small" 
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      sx={{ 
                        color: "#29C0BB",
                        borderColor: "#29C0BB",
                        "&:hover": {
                          borderColor: "#1da8a3",
                        }
                      }}
                    >
                      Change
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'custom1')}
                      />
                    </Button>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: "#999", mb: 1 }} />
                    <Button 
                      component="label" 
                      variant="outlined"
                      size="small"
                      sx={{ 
                        color: "#29C0BB",
                        borderColor: "#29C0BB",
                        "&:hover": {
                          borderColor: "#1da8a3",
                        }
                      }}
                    >
                      Upload Image (max 2MB)
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'custom1')}
                      />
                    </Button>
                  </>
                )}
              </ImageUploadBox>
            </Grid>
            
            <Grid size={{xs: 12, md: 6}}>
              <Typography variant="subtitle1" mb={1}>Custom Image 2</Typography>
              <ImageUploadBox>
                {customImage2Url ? (
                  <>
                    <Box component="img" src={customImage2Url} alt="Custom Image 2" sx={{ height: 60, mb: 1 }} />
                    <Button 
                      component="label" 
                      size="small" 
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      sx={{ 
                        color: "#29C0BB",
                        borderColor: "#29C0BB",
                        "&:hover": {
                          borderColor: "#1da8a3",
                        }
                      }}
                    >
                      Change
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'custom2')}
                      />
                    </Button>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: "#999", mb: 1 }} />
                    <Button 
                      component="label" 
                      variant="outlined"
                      size="small"
                      sx={{ 
                        color: "#29C0BB",
                        borderColor: "#29C0BB",
                        "&:hover": {
                          borderColor: "#1da8a3",
                        }
                      }}
                    >
                      Upload Image (max 2MB)
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'custom2')}
                      />
                    </Button>
                  </>
                )}
              </ImageUploadBox>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Header Preview Dialog */}
        <Dialog
          open={headerPreviewOpen}
          onClose={() => setHeaderPreviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Header Preview
            <Typography variant="caption" display="block" color="text.secondary">
              This is how your header will look in exported documents
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                border: '1px dashed #ccc',
                backgroundColor: '#fff' 
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                {Array.from({ length: 15 }).map((_, index) => {
                  const gridItem = headerGrid[index];
                  const colIndex = Math.floor(index / 5);
                  const rowIndex = index % 5;
                  
                  return (
                    <Box 
                      key={`preview-item-${index}`}
                      sx={{ 
                        gridColumn: colIndex + 1,
                        gridRow: rowIndex + 1,
                        minHeight: '20px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        textAlign: colIndex === 2 ? 'right' : 'left'
                      }}
                    >
                      {gridItem ? renderPreviewItem(gridItem) : <Box sx={{ height: '100%', width: '100%' }} />}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHeaderPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Footer Preview Dialog */}
        <Dialog
          open={footerPreviewOpen}
          onClose={() => setFooterPreviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Footer Preview
            <Typography variant="caption" display="block" color="text.secondary">
              This is how your footer will look in exported documents
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                border: '1px dashed #ccc',
                backgroundColor: '#fff' 
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                {Array.from({ length: 15 }).map((_, index) => {
                  const gridItem = footerGrid[index];
                  const colIndex = Math.floor(index / 5);
                  const rowIndex = index % 5;
                  
                  return (
                    <Box 
                      key={`preview-item-${index}`}
                      sx={{ 
                        gridColumn: colIndex + 1,
                        gridRow: rowIndex + 1,
                        minHeight: '20px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        textAlign: colIndex === 2 ? 'right' : 'left'
                      }}
                    >
                      {gridItem ? renderPreviewItem(gridItem) : <Box sx={{ height: '100%', width: '100%' }} />}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFooterPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default TemplateLayout;